/**
 * Pure composer-state transitions shared by ChatPanel and HeroChat, so the
 * draft-materialization interleavings can be tested without rendering.
 *
 * Both a paste/drop and the first send turn a draft into a real session and
 * both run the carry-over (draft-materialize.ts), in either order — every
 * transition here is idempotent for that reason. A send clears ONLY what it
 * sent: a chip pasted while the session was being created is not its to drop.
 */

/** Set (or, when empty, remove) one key's composer text. */
export function setKeyedText(
  map: Record<string, string>, key: string, text: string | ((prev: string) => string),
): Record<string, string> {
  const next = typeof text === 'function' ? text(map[key] || '') : text
  if (next) return { ...map, [key]: next }
  if (!(key in map)) return map
  const { [key]: _, ...rest } = map
  return rest
}

/** Move the draft's text to the session (`keepText`, an upload) or drop it (a
 *  send — it is the message being sent). No-op once the draft key is gone. */
export function carryDraftText(
  map: Record<string, string>, draftId: string, sid: string, keepText: boolean,
): Record<string, string> {
  if (!(draftId in map)) return map
  const { [draftId]: draftText, ...rest } = map
  return keepText && draftText ? { ...rest, [sid]: draftText } : rest
}

/** Move the draft's list (attachments) to the session, appending to anything
 *  already there. No-op once the draft key is gone. */
export function carryDraftList<T>(
  map: Record<string, T[]>, draftId: string, sid: string,
): Record<string, T[]> {
  const draft = map[draftId]
  if (!draft) return map
  const { [draftId]: _, ...rest } = map
  return draft.length ? { ...rest, [sid]: [...(rest[sid] ?? []), ...draft] } : rest
}

/** The composer's attachments after a send: everything it did NOT send. */
export function withoutSent<T extends { id: string }>(current: T[], sent: readonly T[]): T[] {
  const ids = new Set(sent.map(a => a.id))
  return current.filter(a => !ids.has(a.id))
}

/** A failed send puts back what it removed, never duplicating one still there. */
export function restoreUnsent<T extends { id: string }>(current: T[], sent: readonly T[]): T[] {
  const have = new Set(current.map(a => a.id))
  return [...sent.filter(a => !have.has(a.id)), ...current]
}

/** `restoreUnsent` under an explicit key — a failed send restores into the
 *  chat it was sent from, whichever chat is on screen by the time it fails. */
export function restoreListByKey<T extends { id: string }>(
  map: Record<string, T[]>, key: string, sent: readonly T[],
): Record<string, T[]> {
  const next = restoreUnsent(map[key] ?? [], sent)
  return next.length ? { ...map, [key]: next } : map
}

/** Apply `fn` to the item with this id in WHICHEVER key's list holds it — never
 *  the chat on screen. An upload finishes after the user may have switched chats;
 *  keyed on the visible chat, its "ready" missed the chip, which then spun
 *  forever and (#1292) blocked that chat's Send. `fn` returning `null` removes
 *  the item; a list left empty is dropped. Unchanged map when no list has it. */
export function updateListItemById<T extends { id: string }>(
  map: Record<string, T[]>, id: string, fn: (item: T) => T | null,
): Record<string, T[]> {
  for (const [key, list] of Object.entries(map)) {
    const at = list.findIndex(a => a.id === id)
    if (at < 0) continue
    const next = fn(list[at]!)
    const nextList = next === null ? list.filter((_, i) => i !== at) : list.map((a, i) => (i === at ? next : a))
    if (nextList.length) return { ...map, [key]: nextList }
    const { [key]: _, ...rest } = map
    return rest
  }
  return map
}

/** Set (or, when empty, remove) one key's list. */
export function setKeyedList<T>(
  map: Record<string, T[]>, key: string, list: T[] | ((prev: T[]) => T[]),
): Record<string, T[]> {
  const next = typeof list === 'function' ? list(map[key] ?? []) : list
  if (next.length) return { ...map, [key]: next }
  if (!(key in map)) return map
  const { [key]: _, ...rest } = map
  return rest
}
